import { unwrap } from "@sagot/api-client";
import { OTP_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { PasswordInput } from "../../../../auth/components/password-input";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { downloadText } from "../settings-model";
import { CopyTextButton } from "./panel-kit";

const BACKUP_CODES_FILENAME = "sagot-backup-codes.txt";

/** Authenticator / backup code input (6 digits or a backup code). */
export function CodeInput({ value, onChange, id, allowBackup = false }: { value: string; onChange: (value: string) => void; id?: string; allowBackup?: boolean }) {
  return (
    <Input
      id={id}
      value={value}
      onChange={(event) => { onChange(allowBackup ? event.target.value.toUpperCase().slice(0, 9) : event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH)); }}
      inputMode={allowBackup ? "text" : "numeric"}
      autoComplete="one-time-code"
      placeholder="000000"
      className="w-40 text-center font-mono tracking-[0.3em]"
    />
  );
}

/** Backup codes are shown exactly once (after enabling or regenerating). */
export function BackupCodesDialog({ codes, onClose }: { codes: readonly string[]; onClose: () => void }) {
  const { t } = useTranslation();
  const text = codes.join("\n");
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("settings.twoFactor.codesTitle")}
        description={t("settings.twoFactor.codesBody")}
        closeLabel={t("common.close")}
        footer={<Button onClick={onClose}>{t("settings.twoFactor.codesDone")}</Button>}
      >
        <ul className="grid grid-cols-2 gap-2 font-mono text-sm" data-testid="backup-codes">
          {codes.map((code) => (
            <li key={code} className="rounded-md bg-surface-2 px-3 py-2 text-center">
              {code}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-2">
          <CopyTextButton value={text} />
          <Button variant="secondary" onClick={() => { downloadText(BACKUP_CODES_FILENAME, text); }}>
            <Icon name="download" />
            {t("settings.twoFactor.download")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Turn 2FA off: password + a current code (or backup code). */
export function DisableTwoFactorDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await unwrap(api.POST("/v1/me/2fa/disable", { body: { password, code } }));
      toast.success(t("settings.twoFactor.disabled"));
      await onDone();
      onClose();
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("settings.twoFactor.disableTitle")} description={t("settings.twoFactor.disableBody")} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          <Field label={t("settings.password.current")}>
            <PasswordInput autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); }} />
          </Field>
          <Field label={t("settings.twoFactor.codeLabel")}>
            <CodeInput value={code} onChange={setCode} allowBackup />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="danger" loading={busy} disabled={!password || code.length < OTP_LENGTH}>
              {t("settings.twoFactor.disable")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** New backup codes need a current authenticator code. */
export function RegenerateCodesDialog({ onClose, onCodes }: { onClose: () => void; onCodes: (codes: string[]) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await unwrap(api.POST("/v1/me/2fa/backup-codes", { body: { code } }));
      onCodes(result.backup_codes);
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("settings.twoFactor.regenerateTitle")} description={t("settings.twoFactor.regenerateBody")} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          <Field label={t("settings.twoFactor.codeLabel")}>
            <CodeInput value={code} onChange={setCode} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy} disabled={code.length < OTP_LENGTH}>
              {t("settings.twoFactor.regenerate")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
